"use client";

// Step 8.5 Half A0 commit 0.2 / AC406, AC407: wizard Step 3 - Personal
// event branch. Triggered when businessType==='personal-event'. Event
// type + date (min 7 days out) + headcount/budget + prepay-only
// disclaimer + terms checkbox.

import { useState } from "react";
import { useFormContext, type SubmitErrorHandler } from "react-hook-form";
import type { SignupFormValues } from "./SignupWizard";
import {
  fieldClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "./styles";

const EVENT_TYPES = [
  "Wedding",
  "Quinceanera",
  "Birthday / Anniversary",
  "Corporate event",
  "Funeral / Memorial",
  "Other",
];

export function SignupStep3Personal({
  submitting,
  submitError,
  onBack,
  onSubmit,
  onInvalid,
}: {
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onSubmit: (v: SignupFormValues) => Promise<void> | void;
  onInvalid: SubmitErrorHandler<SignupFormValues>;
}) {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useFormContext<SignupFormValues>();

  // useState lazy initializers are allowed to call impure functions;
  // useMemo callbacks are not (React Compiler rule).
  const [minDateIso] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [minDateMs] = useState(() => Date.now() + 7 * 24 * 60 * 60 * 1000);

  return (
    <form
      data-testid="signup-step-3-personal"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
    >
      <h1 className="mb-2 text-center text-2xl font-semibold text-zinc-900">
        Tell us about your event
      </h1>
      <p className="mb-6 text-center text-sm text-zinc-500">
        A few quick details so we can pack your flowers exactly when you need
        them.
      </p>

      <div
        data-testid="signup-personal-event-banner"
        className="mb-6 flex items-center gap-3 rounded-md border border-[#f5dca0] bg-[#fef7e6] px-5 py-4"
      >
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#f5a623] text-base font-bold text-white">
          i
        </div>
        <div>
          <div className="text-sm font-semibold text-[#6b5618]">
            Personal event detected
          </div>
          <div className="text-sm leading-snug text-[#6b5618]">
            Wholesale prices apply, but Florida sales tax will be added (no
            resale certificate needed).
          </div>
        </div>
      </div>

      <label className={labelClass}>
        Event type <span className="text-red-600">*</span>
        <select
          data-testid="signup-eventType"
          {...register("eventType", { required: "Event type is required." })}
          className={fieldClass}
        >
          <option value="">— Select one —</option>
          {EVENT_TYPES.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        {errors.eventType ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.eventType.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Event date <span className="text-red-600">*</span>
        <input
          type="date"
          data-testid="signup-eventDate"
          min={minDateIso}
          {...register("eventDate", {
            required: "Event date is required.",
            validate: (v) => {
              if (!v) return true;
              const d = new Date(`${v}T00:00:00Z`);
              return (
                d.getTime() >= minDateMs ||
                "We need at least 7 days' notice to source and ship."
              );
            },
          })}
          className={fieldClass}
        />
        <span className="mt-1 text-xs text-zinc-500">
          We need at least 7 days&apos; notice to source and ship.
        </span>
        {errors.eventDate ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.eventDate.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Estimated headcount or budget <span className="text-red-600">*</span>
        <input
          type="text"
          placeholder="150 guests &middot; $1,500 budget"
          data-testid="signup-headcountOrBudget"
          {...register("headcountOrBudget", {
            required: "Estimated headcount or budget is required.",
          })}
          className={fieldClass}
        />
        {errors.headcountOrBudget ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.headcountOrBudget.message as string}
          </span>
        ) : null}
      </label>

      <div className="my-6 rounded-md border-l-4 border-[#77C1BB] bg-zinc-50 p-5 text-sm leading-relaxed text-zinc-700">
        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-900">
          Account terms - personal events
        </div>
        <ul className="ml-5 list-disc">
          <li>$0 credit limit &middot; prepayment required to confirm order</li>
          <li>Single-event account (not recurring)</li>
          <li>Florida sales tax added at checkout</li>
          <li>Account closes 30 days after your event date</li>
        </ul>
      </div>

      <label
        className="mb-6 flex gap-3 rounded-md bg-zinc-50 p-4 text-sm text-zinc-700"
        htmlFor="signup-terms-personal"
      >
        <input
          id="signup-terms-personal"
          type="checkbox"
          data-testid="signup-terms"
          {...register("agreedToTerms", {
            required: "Please agree to the terms.",
          })}
          className="mt-1 accent-[#77C1BB]"
        />
        <span className="flex-1 leading-relaxed">
          I understand this is a one-time personal-event account and by
          creating it I agree to Magnaflor&apos;s{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signup-terms-link"
            className="text-[#77C1BB] underline"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signup-privacy-link"
            className="text-[#77C1BB] underline"
          >
            Privacy Policy
          </a>
          .
        </span>
      </label>
      {errors.agreedToTerms ? (
        <p className="mb-3 text-xs text-red-600">
          {errors.agreedToTerms.message as string}
        </p>
      ) : null}

      {submitError ? (
        <p
          data-testid="signup-submit-error"
          className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {submitError}
        </p>
      ) : null}

      <div className="flex gap-3">
        <button
          type="button"
          data-testid="signup-step-3-back"
          onClick={onBack}
          disabled={submitting}
          className={secondaryButtonClass}
        >
          ← Back
        </button>
        <button
          type="submit"
          data-testid="signup-submit"
          disabled={submitting}
          className={`${primaryButtonClass} flex-1`}
        >
          {submitting ? "Submitting..." : "Submit application →"}
        </button>
      </div>
    </form>
  );
}
